import React from 'react';
import { VStack, Flex, Box, Text, IconButton } from '@chakra-ui/react';
import { FaChevronUp, FaChevronDown, FaGripVertical } from 'react-icons/fa';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 목록이 한 줄이라 끄는 동안 카드가 옆으로 흔들리지 않도록 세로로만 움직인다
const restrictToVerticalAxis = ({ transform }) => ({ ...transform, x: 0 });

// 배송 순서 편집 목록.
// 멀리 옮길 때는 손잡이(≡)를 끌고, 한 칸씩 맞출 때는 화살표를 쓴다.
// 장갑을 끼면 짧게 끄는 동작이 어려워 화살표도 함께 둔다.
const DeliveryOrderEditor = ({ deliveries, onMove }) => {
  const sensors = useSensors(
    // 손잡이를 잠깐 누르고 있어야 끌기가 시작된다. 스치듯 닿은 터치로 순서가 바뀌지 않게
    useSensor(PointerSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    const fromIndex = deliveries.findIndex(delivery => delivery.delivery_id === active.id);
    const toIndex = deliveries.findIndex(delivery => delivery.delivery_id === over.id);
    if (fromIndex !== -1 && toIndex !== -1) {
      onMove(fromIndex, toIndex);
    }
  };

  return (
    <VStack spacing={2} align="stretch" px={2}>
      <Text fontSize="sm" color="gray.500" px={2}>
        손잡이를 끌거나 화살표를 누르면 이 기기에 바로 저장됩니다.
      </Text>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={deliveries.map(delivery => delivery.delivery_id)}
          strategy={verticalListSortingStrategy}
        >
          {deliveries.map((delivery, index) => (
            <SortableDeliveryRow
              key={delivery.delivery_id}
              delivery={delivery}
              index={index}
              isLast={index === deliveries.length - 1}
              onMove={onMove}
            />
          ))}
        </SortableContext>
      </DndContext>
    </VStack>
  );
};

function SortableDeliveryRow({ delivery, index, isLast, onMove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: delivery.delivery_id });

  return (
    <Flex
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      position="relative"
      zIndex={isDragging ? 1 : 'auto'}
      align="center"
      gap={2}
      p={2}
      bg="white"
      borderRadius="md"
      boxShadow={isDragging ? 'lg' : 'sm'}
      borderWidth="1px"
      borderColor={isDragging ? '#E9673D' : 'gray.100'}
    >
      {/* 끌기는 손잡이에서만 시작된다. 카드 나머지 부분은 평소처럼 화면을 스크롤한다 */}
      <IconButton
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        icon={<FaGripVertical />}
        aria-label="끌어서 순서 바꾸기"
        variant="ghost"
        color="gray.400"
        minW="32px"
        cursor={isDragging ? 'grabbing' : 'grab'}
        // 손잡이 위에서는 브라우저 스크롤 대신 끌기가 동작해야 한다
        sx={{ touchAction: 'none' }}
      />
      <Text fontWeight="bold" color="#E9673D" minW="20px" textAlign="center">
        {index + 1}
      </Text>
      <Box flex="1" minW={0}>
        <Text noOfLines={1}>{delivery.b_nm}</Text>
        <Text fontSize="sm" color="gray.500" noOfLines={1}>{delivery.address}</Text>
      </Box>
      <IconButton
        icon={<FaChevronUp />}
        aria-label="위로"
        variant="outline"
        isDisabled={index === 0}
        onClick={() => onMove(index, index - 1)}
      />
      <IconButton
        icon={<FaChevronDown />}
        aria-label="아래로"
        variant="outline"
        isDisabled={isLast}
        onClick={() => onMove(index, index + 1)}
      />
    </Flex>
  );
}

export default DeliveryOrderEditor;
